import React, { useEffect, useRef, useState } from "react";

import { yupResolver } from "@hookform/resolvers/yup";
import {
  Button,
  Divider,
  Flex,
  Form,
  InlineAlert,
  Page,
  Typography,
} from "@samskip/frontend-components";
import { useForm } from "react-hook-form";
import { Trans, useTranslation } from "react-i18next";
import { toast } from "react-toastify";

import { useAppSelector } from "@assets/hooks";
import { SpinnerPage } from "@components";
import { userProfileState } from "@state/slices/userProfileSlice";

import CompanySection from "../components/domestic-calculator/CompanySection";
import GoodsCharacteristicsSection from "../components/domestic-calculator/GoodsCharacteristicsSection";
import PriceResultSection from "../components/domestic-calculator/PriceResultSection";
import RouteSection from "../components/domestic-calculator/RouteSection";
import { CalculatePriceTranslation } from "../constants";
import { CalculatorForm, defaultCalculatorFormValues } from "../formValues";
import { hasCompanyInDomesticSystem } from "../helpers";
import { calculatorForm_To_CalculatePriceRequest } from "../mappers";
import { createCalculatorFormSchema } from "../schemas";
import { calculatePrice, getLocations } from "../services";
import { DomesticPriceAddress, PriceResult } from "../types";

const CalculatePricePage = () => {
  const { t } = useTranslation();
  const { userProfile, profileLoaded } = useAppSelector(userProfileState);

  const [locations, setLocations] = useState<DomesticPriceAddress[]>([]);
  const [isLoadingLocations, setIsLoadingLocations] = useState(true);
  const [isCheckingCompany, setIsCheckingCompany] = useState(true);
  const [isCalculating, setIsCalculating] = useState(false);
  const [priceResult, setPriceResult] = useState<PriceResult | null>(null);
  const [hasCompanyPricing, setHasCompanyPricing] = useState(false);

  const {
    control,
    formState: { errors },
    getValues,
    handleSubmit,
    register,
    setValue,
    reset,
    trigger,
    watch,
  } = useForm<CalculatorForm>({
    defaultValues: defaultCalculatorFormValues,
    resolver: yupResolver(createCalculatorFormSchema() as any),
    mode: "onTouched",
  });

  const isUseCompany = watch("UseCompany");
  const isReady = !isLoadingLocations && !isCheckingCompany;
  const isMounted = useRef(true);

  useEffect(() => {
    isMounted.current = true;
    return () => {
      isMounted.current = false;
    };
  }, []);

  // Load locations once
  useEffect(() => {
    getLocations()
      .then((data) => setLocations(data))
      .catch(() => toast.error(t("ERROR_GENERAL")))
      .finally(() => setIsLoadingLocations(false));
  }, []);

  // Check company when profile is ready
  useEffect(() => {
    if (!profileLoaded) {
      // Guard against profileLoaded never resolving
      const timeout = setTimeout(() => {
        if (isMounted.current) setIsCheckingCompany(false);
      }, 10000);
      return () => clearTimeout(timeout);
    }

    if (!userProfile) {
      setHasCompanyPricing(false);
      setIsCheckingCompany(false);
      return;
    }

    reset(defaultCalculatorFormValues, {
      keepTouched: false,
      keepErrors: false,
    });
    setHasCompanyPricing(false);
    setIsCheckingCompany(true);
    setPriceResult(null);

    // Track this specific check so stale responses from earlier company switches are ignored
    let cancelled = false;

    hasCompanyInDomesticSystem(userProfile.SelectedCompany.CompanyRegNo)
      .then((exists) => {
        if (cancelled || !isMounted.current) return;
        setHasCompanyPricing(exists);
        if (exists) setValue("UseCompany", true);
      })
      .finally(() => {
        if (!cancelled && isMounted.current) setIsCheckingCompany(false);
      });

    return () => {
      cancelled = true;
    };
  }, [profileLoaded, userProfile?.SelectedCompany.CompanyRegNo]);

  const onSubmit = async (data: CalculatorForm) => {
    setIsCalculating(true);
    setPriceResult(null);
    try {
      const result = await calculatePrice(
        calculatorForm_To_CalculatePriceRequest(data)
      );
      setPriceResult(result);
    } catch {
      toast.error(t("ERROR_GENERAL"));
    } finally {
      setIsCalculating(false);
    }
  };

  if (!isReady) {
    return <SpinnerPage />;
  }

  return (
    <Page size="medium" title={t(CalculatePriceTranslation.PAGE_TITLE)}>
      <Form id="calculate-price-form">
        {/* Description */}
        <Typography>{t(CalculatePriceTranslation.DESCRIPTION)}</Typography>

        {/* Company pricing — authenticated only */}
        {hasCompanyPricing && userProfile && (
          <CompanySection
            isCalculating={isCalculating}
            isUseCompany={isUseCompany}
            register={register}
            userProfile={userProfile}
          />
        )}
        <RouteSection
          control={control}
          errors={errors}
          getValues={getValues}
          isCalculating={isCalculating}
          isLoadingLocations={isLoadingLocations}
          locations={locations}
          trigger={trigger}
        />

        <GoodsCharacteristicsSection
          errors={errors}
          isCalculating={isCalculating}
          register={register}
        />
        <InlineAlert variant="info">
          <Typography variant="body-xs">
            <Trans
              components={[
                <a
                  href="https://www.samskip.is"
                  key="price-list"
                  rel="noopener noreferrer"
                  style={{ fontWeight: "bold" }}
                  target="_blank"
                />,
                <a
                  href="https://www.samskip.is/flutningar/innanlands/tilbod-innanlandsflutningur/"
                  key="quote"
                  rel="noopener noreferrer"
                  style={{ fontWeight: "bold" }}
                  target="_blank"
                />,
              ]}
              i18nKey={CalculatePriceTranslation.PRICE_REQUEST_DISCLAIMER}
              values={{ priceListLink: "www.samskip.is" }}
            />
          </Typography>
        </InlineAlert>
        <Flex gap="small" justifyContent="start">
          <Button
            disabled={isCalculating}
            id="calculate"
            loading={isCalculating}
            onClick={handleSubmit(onSubmit)}
            variant="primary"
          >
            {t(CalculatePriceTranslation.CALCULATE_PRICE)}
          </Button>
        </Flex>

        {/* Inline results */}
        {priceResult && <PriceResultSection result={priceResult} />}
        <Divider />
        {/* Contact footer */}
        <Typography variant="info">
          <Trans
            components={{ bold: <strong /> }}
            i18nKey={CalculatePriceTranslation.TEXT_CONTACT_CUSTOMER_SERVICE}
            values={{ e: "innanlands@samskip.is" }}
          />
        </Typography>
      </Form>
    </Page>
  );
};

export default CalculatePricePage;
